import Alert from '~/components/Alert';
import Submit from '~/components/button/Submit';
import Form from '~/components/Form';

import FileUploader from '~/components/form-controls/FileUploader';
import Input from '~/components/form-controls/Input';

import LinkButton from '~/components/button/LinkButton';
import CustomerInfoFields from '~/components/CustomerInfoFields';
import CustomField from '~/components/CustomField';
import Checkbox from '~/components/form-controls/Checkbox';
import Combobox from '~/components/form-controls/Combobox';
import Icon from '~/components/Icon';
import Page from '~/components/layout/page/Page';
import LoadingBoundary from '~/components/LoadingBoundary';
import { formatDate } from '~/utils/formatters';
import { FormKeys, useActionResult, useLoaderData } from './customerInfo';

const analyzeProductMessages = [
  'Matching your products',
  'Searching the catalog',
  'Identifying details',
  'Almost there',
];

export default function CustomerInfoPage() {
  const {
    storeSalesChannels,
    shouldCollectProofOfPurchase,
    shouldCollectAddress,
    customFields,
    draft,
    uploadFileInfo: uploadFiles,
    requirePhone,
    countryCodes,
    shouldCollectRegistrationEmailMarketingConsent,
    emailMarketingConsentPolicyUrl,
    emailMarketingConsentText,
    emailMarketingConsentDescriptionText,
    emailMarketingConsentPolicyUrlTextTitle,
    suggestedRegistrationMessage,
  } = useLoaderData();

  const requiredMediaCount = Number(shouldCollectProofOfPurchase);

  const actionResult = useActionResult();
  const hasErrors = actionResult && !actionResult.ok;

  const showAlert = hasErrors && actionResult.message;

  // ? we must have all the fields set to show the email marketing consent
  const collectEmailMarketingConsent =
    shouldCollectRegistrationEmailMarketingConsent &&
    emailMarketingConsentText &&
    emailMarketingConsentDescriptionText &&
    emailMarketingConsentPolicyUrl &&
    emailMarketingConsentPolicyUrlTextTitle;
  const maxDate = new Date().toISOString();

  return (
    <Page.Root title="Registration Information">
      <LoadingBoundary loadingMessages={analyzeProductMessages} interval={3000}>
        {suggestedRegistrationMessage && (
          <div className="flex flex-row items-center justify-center gap-4">
            <Icon icon="SparklesSolid" className="size-6" />
            <p>{suggestedRegistrationMessage}</p>
          </div>
        )}
        <Form encType="multipart/form-data">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CustomerInfoFields
              className="sm:col-span-2"
              info={{
                email: draft?.email,
                firstName: draft?.firstName,
                lastName: draft?.lastName,
                address: draft?.address,
              }}
              countryCodes={countryCodes}
              requirePhone={requirePhone}
              omitAddress={!shouldCollectAddress}
            />

            <Input
              type="date"
              name={FormKeys.EstimatedPurchaseDate}
              required
              id="estimated-purchase-date"
              label="Estimated Purchase Date"
              defaultValue={draft?.estimatedPurchaseDate}
              max={maxDate.split('T')[0]}
              validationMessages={{
                rangeOverflow: `Please choose a date no later than ${formatDate(maxDate)}`,
              }}
            />

            <Combobox
              id="purchase-location"
              label="Original Purchase Location"
              name={FormKeys.StoreSalesChannel}
              required
              defaultValue={
                draft?.storeSalesChannel
                  ? `${draft.storeSalesChannel.id}`
                  : undefined
              }
              options={storeSalesChannels.map((option) => ({
                value: `${option.id}`,
                keywords: [option.name],
                label: option.name,
              }))}
            />

            <Input
              id="orderNumber"
              className="sm:col-span-2"
              label=" Original Order Number"
              name={FormKeys.OrderNumber}
              defaultValue={draft?.orderNumber}
            />
            {customFields.map((field) => (
              <CustomField
                key={field.id}
                className="sm:col-span-2"
                field={field}
                defaultValue={draft?.customFields[field.id]?.value}
              />
            ))}

            <FileUploader
              label="Proof of Purchase"
              className="sm:col-span-2"
              destination={uploadFiles.destination}
              storefrontId={uploadFiles.storefrontId}
              name={FormKeys.Assets}
              min={requiredMediaCount}
              description="Upload a receipt, invoice, or other proof of purchase."
              defaultValue={draft?.proofOfPurchaseAssets ?? []}
            />
            <input
              type="hidden"
              data-testid="required-asset-count"
              name={FormKeys.RequiredAssetCount}
              value={requiredMediaCount}
            />
          </div>

          {collectEmailMarketingConsent && (
            <Checkbox
              id="marketing-consent"
              name={FormKeys.EmailMarketingConsent}
              className="sm:col-span-2"
              value="on"
              defaultChecked
              description={
                <span className="flex flex-col items-start gap-1">
                  {emailMarketingConsentDescriptionText}
                  <LinkButton
                    to={emailMarketingConsentPolicyUrl}
                    variant="ghost"
                    target="_blank"
                  >
                    {' '}
                    {emailMarketingConsentPolicyUrlTextTitle}
                  </LinkButton>
                </span>
              }
              label={emailMarketingConsentText}
            />
          )}

          {showAlert && <Alert variant="error" title={actionResult.message} />}

          <Submit>Next</Submit>
        </Form>
      </LoadingBoundary>
    </Page.Root>
  );
}
